import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import KazemLogo from "@/components/KazemLogo";
import LangSwitch from "@/components/LangSwitch";
import { Menu, X, ArrowRight, Mail, ChevronDown, Lock } from "lucide-react";
import { CONTACT_EMAIL, SERVICES } from "@/lib/siteData";
import { useLang } from "@/context/LangContext";

const navCls = ({ isActive }) =>
    `relative py-5 text-[13px] font-medium whitespace-nowrap transition-colors ${isActive ? "text-[#f26b1d] after:absolute after:bottom-3 after:left-0 after:right-0 after:h-0.5 after:bg-[#f26b1d]" : "text-[#0b2545] hover:text-[#f26b1d]"}`;

function ServicesMenu() {
    const { t } = useLang();
    return (
        <div className="relative group">
            <NavLink to="/services" data-testid="nav-link-services" className={(p) => `${navCls(p)} inline-flex items-center gap-1`}>
                {t("Nos services", "Our services")} <ChevronDown size={14} />
            </NavLink>
            <div className="absolute left-0 top-full hidden group-hover:block bg-white shadow-xl rounded-xl border border-slate-100 py-2 w-72 z-50">
                {SERVICES.map((s) => (
                    <Link key={s.slug} to={`/services#${s.slug}`} data-testid={`nav-service-${s.slug}`} className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#0b2545] hover:bg-orange-50 hover:text-[#f26b1d]">
                        <s.icon size={16} className="text-[#f26b1d]" /> {t(s.title, s.titleEn)}
                    </Link>
                ))}
            </div>
        </div>
    );
}

export default function PublicHeader() {
    const [open, setOpen] = useState(false);
    const { t } = useLang();
    const links = [
        { to: "/a-propos", label: t("À propos", "About us"), testid: "nav-link-apropos" },
        { to: "/engagement", label: t("Notre engagement", "Our commitment"), testid: "nav-link-engagement" },
        { to: "/actualites", label: t("Actualités", "News"), testid: "nav-link-actualites" },
        { to: "/suivi", label: t("Suivi", "Tracking"), testid: "nav-link-suivi" },
        { to: "/contact", label: "Contact", testid: "nav-link-contact" },
    ];
    const home = { to: "/", label: t("Accueil", "Home"), testid: "nav-link-accueil" };
    return (
        <header className="fixed top-0 inset-x-0 z-50 bg-white/95 backdrop-blur border-b border-slate-100 shadow-sm">
            <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between gap-5">
                <Link to="/" data-testid="kazem-logo-link" className="shrink-0"><KazemLogo /></Link>
                <nav className="hidden xl:flex items-center gap-5">
                    <NavLink to="/" end className={navCls} data-testid={home.testid}>{home.label}</NavLink>
                    <ServicesMenu />
                    {links.map((l) => (
                        <NavLink key={l.to} to={l.to} className={navCls} data-testid={l.testid}>{l.label}</NavLink>
                    ))}
                </nav>
                <div className="hidden xl:flex items-center gap-4">
                    <LangSwitch />
                    <Link to="/tms/login" data-testid="nav-link-tms-login-compact" className="2xl:hidden text-slate-500 hover:text-[#f26b1d]" aria-label="TMS"><Lock size={16} /></Link>
                    <Link to="/devis" data-testid="header-devis-button" className="kz-btn-primary text-[13px] whitespace-nowrap">
                        {t("Demander un devis", "Request a quote")} <ArrowRight size={15} />
                    </Link>
                    <div className="text-[12px] text-[#0b2545] leading-5 whitespace-nowrap hidden 2xl:block">
                        <a href={`mailto:${CONTACT_EMAIL}`} data-testid="header-email" className="flex items-center gap-2 hover:text-[#f26b1d]"><Mail size={13} />{CONTACT_EMAIL}</a>
                        <Link to="/tms/login" data-testid="nav-link-tms-login" className="flex items-center gap-2 text-slate-500 hover:text-[#f26b1d]"><Lock size={12} />{t("Espace TMS", "TMS area")}</Link>
                    </div>
                </div>
                <div className="xl:hidden flex items-center gap-3">
                    <LangSwitch testid="lang-switch-mobile" />
                    <button data-testid="public-mobile-menu-toggle" className="text-[#0b2545]" onClick={() => setOpen((o) => !o)} aria-label="Menu">
                        {open ? <X /> : <Menu />}
                    </button>
                </div>
            </div>
            {open && (
                <div className="xl:hidden border-t border-slate-100 bg-white">
                    <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-1 text-[#0b2545]">
                        {[home, { to: "/services", label: t("Nos services", "Our services"), testid: "nav-link-services" }, ...links].map((l) => (
                            <Link key={l.to} to={l.to} data-testid={`${l.testid}-mobile`} className="py-2 font-medium" onClick={() => setOpen(false)}>{l.label}</Link>
                        ))}
                        <Link to="/tms/login" className="py-2 text-slate-500" onClick={() => setOpen(false)}>{t("Espace TMS", "TMS area")}</Link>
                        <Link to="/devis" className="kz-btn-primary w-fit mt-2" onClick={() => setOpen(false)}>{t("Demander un devis", "Request a quote")} <ArrowRight size={15} /></Link>
                    </div>
                </div>
            )}
        </header>
    );
}
